import React, { useState } from "react";
import Calendar from "@components/common/Calendar.jsx";
import ResourceUploadField from "@components/common/ResourceUploadField.jsx";
import { useAuthStore } from "@/store/useAuthStore.jsx";
import moment from "moment";

/**
 * 포토 필터 추가/수정 모달 컴포넌트
 * @param {Object} props - 컴포넌트 속성
 * @param {string} props.title - 모달 제목
 * @param {boolean} props.isVisible - 모달 표시 여부
 * @param {Object} props.inputs - 모달 입력값 상태
 * @param {Function} props.setInputs - 모달 입력값 설정 함수
 * @param {Function} props.onClose - 모달 닫기 함수
 * @param {Function} props.onConfirm - 확인 버튼 클릭 시 실행 함수
 * @param {Function} props.onCheckDuplicate - 중복 확인 함수
 * @param {Array} props.availableBranches - 사용 가능한 지점 목록
 * @param {Array} props.selectedBranches - 선택된 지점 목록
 * @param {Function} props.onAddBranch - 지점 추가 함수
 * @param {Function} props.onRemoveBranch - 지점 제거 함수
 * @param {Function} props.onBranchSelect - 지점 선택 함수
 * @param {string} props.selectedAvailableBranch - 선택된 사용 가능 지점 ID
 * @param {string} props.selectedAddedBranch - 선택된 추가된 지점 ID
 * @param {boolean} props.isEdit - 수정 모드 여부
 * @param {Function} props.onFileImport - 파일 불러오기 함수 (기존 방식)
 * @param {Function} props.onResourceUploaded - 리소스 업로드 완료 함수 (공통 방식)
 */
const PhotoFilterInputModal = ({
    title,
    isVisible,
    inputs,
    setInputs,
    onClose,
    onConfirm,
    onCheckDuplicate,
    availableBranches = [],
    selectedBranches = [],
    onAddBranch,
    onRemoveBranch,
    onBranchSelect,
    selectedAvailableBranch,
    selectedAddedBranch,
    isEdit = false,
    onFileImport,
    onResourceUploaded,
}) => {

    // useAuthStore 훅 사용
    const { user } = useAuthStore();

    // 달력 관련 상태
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarPosition, setCalendarPosition] = useState({
        top: 0,
        left: 0,
    });
    const [activeInput, setActiveInput] = useState(null);

    // 다중 선택을 위한 상태
    const [selectedAvailableBranches, setSelectedAvailableBranches] = useState([]);
    const [selectedAddedBranches, setSelectedAddedBranches] = useState([]);

    if (!isVisible) return null;

    // 모달 입력값 변경 핸들러
    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        
        if (type === 'file') {
            const file = files[0];
            setInputs(prev => ({
                ...prev,
                [name]: file,
                cameraData: file ? file.name : ''
            }));
        } else {
            setInputs(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    // 달력 선택 핸들러
    const handleDateClick = (dateType, event) => {
        const input = event.target;
        const rect = input.getBoundingClientRect();

        setCalendarPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left,
        });

        setActiveInput(dateType);
        setShowCalendar(true);
    };

    // 날짜 선택 핸들러
    const handleDateSelect = (startDate, endDate) => {
        // 활성화된 입력 필드에 따라 시작일 또는 종료일만 업데이트
        if (activeInput === 'startDate') {
            // 시작일 입력 필드가 활성화된 경우
            let _startDate = '';
            if (moment(startDate).isValid()) {
                _startDate = moment(startDate).format('YYYYMMDD');
            }

            setInputs(prev => ({
                ...prev,
                startDatetime: _startDate
            }));
        } else if (activeInput === 'endDate') {
            // 종료일 입력 필드가 활성화된 경우
            let _endDate = '';
            // 여기서 중요: 종료일의 경우 두 번째 인수인 endDate을 사용해야 함
            // 하지만 단일 날짜 선택의 경우 startDate에 선택된 날짜가 들어있음
            const dateToUse = endDate || startDate;
            if (moment(dateToUse).isValid()) {
                _endDate = moment(dateToUse).format('YYYYMMDD');
            }

            setInputs(prev => ({
                ...prev,
                endDatetime: _endDate
            }));
        }

        setShowCalendar(false);
    };

    // 다중 선택 핸들러
    const handleBranchCheckboxChange = (type, branchId) => {
        if (type === "available") {
            setSelectedAvailableBranches((prev) => {
                if (prev.includes(branchId)) {
                    return prev.filter((id) => id !== branchId);
                } else {
                    return [...prev, branchId];
                }
            });
        } else {
            setSelectedAddedBranches((prev) => {
                if (prev.includes(branchId)) {
                    return prev.filter((id) => id !== branchId);
                } else {
                    return [...prev, branchId];
                }
            });
        }
    };

    // 다중 추가 핸들러
    const handleMultiAddBranch = () => {
        if (selectedAvailableBranches.length === 0) return;

        const branchesToAdd = availableBranches.filter(
            (b) =>
                selectedAvailableBranches.includes(b.id) &&
                !selectedBranches.some((sb) => sb.id === b.id)
        );

        if (branchesToAdd.length > 0) {
            onAddBranch(branchesToAdd);
            setSelectedAvailableBranches([]);
        }
    };

    // 다중 제거 핸들러
    const handleMultiRemoveBranch = () => {
        if (selectedAddedBranches.length === 0) return;
        onRemoveBranch(selectedAddedBranches);
        setSelectedAddedBranches([]);
    };

    // 공통 리소스 업로드 처리 함수
    const handleResourceUploaded = (resourceId) => {
        if (onResourceUploaded) {
            onResourceUploaded(resourceId);
        }
        setInputs(prev => ({
            ...prev,
            filterDataResourceId: resourceId
        }));
    };

    return (
        <div className="modal-overlay active" id={isEdit ? "edit-modal" : "create-modal"}>
            <div className="modal-container">
                <div className="modal-header">
                    <h3 className="modal-title">{title}</h3>
                    <button className="modal-close" onClick={onClose}>
                        &times;
                    </button>
                </div>
                <div className="modal-body">
                    {/* 작성자 */}
                    <div className="modal-form-group">
                        <label className="modal-label">작성자</label>
                        <input
                            type="text"
                            className="modal-input"
                            name="author"
                            value={user?.name || ''}
                            readOnly
                        />
                    </div>

                    {/* 필터 데이터 (공통 리소스 업로드) */}
                    <div className="modal-form-group">
                        <label className="modal-label">포토 필터 데이터</label>
                        <ResourceUploadField
                            label=""
                            resourceId={inputs.filterDataResourceId}
                            onResourceUploaded={handleResourceUploaded}
                            acceptTypes="*/*"
                            placeholder="포토 필터 데이터 파일을 선택하세요"
                        />
                    </div>

                    {/* 필터명 */}
                    <div className="modal-form-group">
                        <label className="modal-label">필터명</label>
                        <div className="modal-input-with-button">
                            <input
                                type="text"
                                className="modal-input"
                                name="filterName"
                                value={inputs.filterName || ''}
                                onChange={handleInputChange}
                                placeholder="필터명을 입력하세요"
                            />
                            <button
                                className="modal-btn modal-btn-secondary"
                                onClick={onCheckDuplicate}
                            >
                                중복확인
                            </button>
                        </div>
                    </div>

                    {/* 사용기간 */}
                    <div className="modal-form-group">
                        <label className="modal-label">사용기간</label>
                        <div className="date-range-container">
                            <input
                                type="text"
                                className="modal-input date-input"
                                id="start-date"
                                name="startDatetime"
                                value={inputs.startDatetime || ''}
                                placeholder="시작일"
                                readOnly
                                onClick={(e) =>
                                    handleDateClick("startDate", e)
                                }
                            />
                            <span className="date-separator">-</span>
                            <input
                                type="text"
                                className="modal-input date-input"
                                id="end-date"
                                name="endDatetime"
                                value={inputs.endDatetime || ''}
                                placeholder="종료일"
                                readOnly
                                onClick={(e) =>
                                    handleDateClick("endDate", e)
                                }
                            />
                        </div>
                    </div>

                    {/* 사용 지점 */}
                    {/*<div className="modal-form-group">*/}
                    {/*    <label className="modal-label">사용 지점</label>*/}
                    {/*    <div className="permission-transfer-container">*/}
                    {/*        <div className="permission-box">*/}
                    {/*            <div className="permission-header">사용 가능 지점</div>*/}
                    {/*            <div className="permission-list">*/}
                    {/*                {availableBranches.map((branch) => (*/}
                    {/*                    <div*/}
                    {/*                        key={branch.id}*/}
                    {/*                        className={`permission-item ${*/}
                    {/*                            selectedAvailableBranches.includes(branch.id) ? "selected" : ""*/}
                    {/*                        }`}*/}
                    {/*                    >*/}
                    {/*                        <input*/}
                    {/*                            type="checkbox"*/}
                    {/*                            id={`available_${branch.id}`}*/}
                    {/*                            className="permission-checkbox"*/}
                    {/*                            checked={selectedAvailableBranches.includes(branch.id)}*/}
                    {/*                            onChange={() =>*/}
                    {/*                                handleBranchCheckboxChange("available", branch.id)*/}
                    {/*                            }*/}
                    {/*                        />*/}
                    {/*                        <label htmlFor={`available_${branch.id}`}>*/}
                    {/*                            {branch.name}*/}
                    {/*                        </label>*/}
                    {/*                    </div>*/}
                    {/*                ))}*/}
                    {/*            </div>*/}
                    {/*        </div>*/}

                    {/*        <div className="permission-controls">*/}
                    {/*            <button*/}
                    {/*                className="permission-control-btn"*/}
                    {/*                onClick={handleMultiAddBranch}*/}
                    {/*            >*/}
                    {/*                <i className="ri-arrow-right-line"></i>*/}
                    {/*            </button>*/}
                    {/*            <button*/}
                    {/*                className="permission-control-btn"*/}
                    {/*                onClick={handleMultiRemoveBranch}*/}
                    {/*            >*/}
                    {/*                <i className="ri-arrow-left-line"></i>*/}
                    {/*            </button>*/}
                    {/*        </div>*/}

                    {/*        <div className="permission-box">*/}
                    {/*            <div className="permission-header">선택된 지점</div>*/}
                    {/*            <div className="permission-list">*/}
                    {/*                {selectedBranches.map((branch) => (*/}
                    {/*                    <div*/}
                    {/*                        key={branch.id}*/}
                    {/*                        className={`permission-item ${*/}
                    {/*                            selectedAddedBranches.includes(branch.id) ? "selected" : ""*/}
                    {/*                        }`}*/}
                    {/*                    >*/}
                    {/*                        <input*/}
                    {/*                            type="checkbox"*/}
                    {/*                            id={`selected_${branch.id}`}*/}
                    {/*                            className="permission-checkbox"*/}
                    {/*                            checked={selectedAddedBranches.includes(branch.id)}*/}
                    {/*                            onChange={() =>*/}
                    {/*                                handleBranchCheckboxChange("added", branch.id)*/}
                    {/*                            }*/}
                    {/*                        />*/}
                    {/*                        <label htmlFor={`selected_${branch.id}`}>*/}
                    {/*                            {branch.name}*/}
                    {/*                        </label>*/}
                    {/*                    </div>*/}
                    {/*                ))}*/}
                    {/*            </div>*/}
                    {/*        </div>*/}
                    {/*    </div>*/}
                    {/*</div>*/}

                    {/* 배포 여부 */}
                    <div className="modal-form-group toggle-group">
                        <div className="toggle-label">배포 여부</div>
                        <div className="toggle-switch-container">
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    name="isDeployed"
                                    checked={inputs.isDeployed || false}
                                    onChange={handleInputChange}
                                />
                                <span className="toggle-slider"></span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button
                        className="modal-btn modal-btn-cancel"
                        onClick={onClose}
                    >
                        취소
                    </button>
                    <button
                        className="modal-btn modal-btn-primary"
                        onClick={onConfirm}
                    >
                        확인
                    </button>
                </div>
            </div>

            {/* 달력 컴포넌트 */}
            {showCalendar && (
                <Calendar
                    onDateSelect={handleDateSelect}
                    onClose={() => setShowCalendar(false)}
                    position={calendarPosition}
                />
            )}
        </div>
    );
};

export default PhotoFilterInputModal;
